<template>
  <va-modal
      id="update-appointment-status-modal"
      ref="updateAppointmentStatusModal"
      :value="isOpen"
      :okText="$t('modals.buttons.save')"
      :okDisabled="isSubmitingForm"
      @ok="submit"
      :cancelText="$t('modals.buttons.back')"
      @cancel="onCancel"
      :ok-disabled="selectedStatus === null"
    >

    <div slot="header" class="modal-title">
        <span>{{ $t('homelab.update_appointment_status_title', {name: appointment.name})}}</span>
    </div>

    <div id="appointment-info">

      <div
        class="d-flex justify-content-between align-items-center mb-3"
        @click="isShowingAppointmentInfo = !isShowingAppointmentInfo"
      >
        <h4 class="mb-0">{{$t('homelab.new_status')}}</h4>

        <div class="d-flex justify-content-end align-items-center">
          <span class="text-primary clickable">
            {{isShowingAppointmentInfo ? $t('homelab.buttons.hide_appointment_info'): $t('homelab.buttons.see_appointment_info')}}
          </span>
        </div>

      </div>

      <fade-transition :duration="350" mode="in-out">
        <AppointmentShortInfo
          v-if="isShowingAppointmentInfo"
          :appointment="appointment"
          key="appointment-short-info"
        />
      </fade-transition>

    </div>

    <hr>
    <form class="evollu-form">

      <div class="form-row">
        <div class="col-12 col-md-6 mb-3">
          <DatePicker
            id="date-picker-scheduled-time"
            v-model="form.data.date"
            :label="$t('homelab.labels.status_date')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            :clearable="false"
            @input="form.errors.clear('date')"
            :error="form.errors.has('date')"
            :error-messages="[$t(form.errors.get('date'))]"
          />
        </div>

        <div class="col-12 col-md-6 mb-3">
          <label class="form-label" :class="{'text-danger': form.errors.has('status_id')}">
            {{ $t('homelab.labels.status') }}:
          </label>
          <multiselect
            id="appointment-status"
            v-model="selectedStatus"
            :options="availableStatuses"
            track-by="id"
            label="name"
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            @input="form.errors.clear('status_id')"
            >
            <template slot="option" slot-scope="{option}">
              {{ $t(`homelab.statuses.${option.id}`) }}
            </template>
            <template slot="singleLabel" slot-scope="{option}">
              {{ $t(`homelab.statuses.${option.id}`) }}
            </template>
          </multiselect>
          <div class="text-danger" v-if="form.errors.has('status_id')">
            <small>{{$t(form.errors.get('status_id'))}}</small>
          </div>
        </div>
      </div>

      <fade-transition :duration="350" mode="in-out">
        <div class="form-row" v-if="isRescheduling" key="appointment-schedule-form">
          <div class="col-12 col-md-6 mb-3">
            <date-picker
              id="date-picker-scheduled-time"
              v-model="scheduledTime"
              :label="$t('homelab.labels.scheduled_time_to_appoint')"
              :config="{enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true, minDate: 'today', defaultHour: 9}"
              :clearable="false"
              @input="form.errors.clear('scheduled_time_start'); form.errors.has('scheduled_time_end')"
              :error="form.errors.has('scheduled_time_start')"
              :error-messages="[$t(form.errors.get('scheduled_time_start'))]"
            />
          </div>

          <div class="col-12 col-md-6 mb-3">
            <label class="form-label">
              {{ $t('homelab.labels.schedule_time_interval') }}:
            </label>
            <multiselect
              id="appointment-scheduled-time"
              v-model="selectedDuration"
              :options="durations"
              track-by="id"
              label="value"
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              @input="form.errors.clear('gender')"
              >
              <template slot="option" slot-scope="{option}">
                {{ $t(`homelab.pickup_durations.${option.id}`) }}
              </template>
              <template slot="singleLabel" slot-scope="{option}">
                {{ $t(`homelab.pickup_durations.${option.id}`) }}
              </template>
            </multiselect>
          </div>

          <div v-if="formattedCurrentScheduleWarning && isRescheduling"
            class="col-12 mb-3 text-primary text-danger"
          >
            <small>{{formattedCurrentScheduleWarning}}</small>
          </div>

        </div>
      </fade-transition>

      <div class="form-row">
        <div class="mb-3 col-12">
          <va-input
            id="appointment-status-private-commentary"
            :label="$t('homelab.labels.private_commentary')"
            type="textarea"
            :min-rows="3"
            :placeholder="$t('homelab.messages.private_commentary_hint')"
            v-model="form.data.private_commentary"
            @input="form.errors.clear('private_commentary')"
            :error="form.errors.has('private_commentary')"
            :error-messages="[$t(form.errors.get('private_commentary'))]"
          />
        </div>

        <div class="mb-3 col-12">
          <va-input
            id="appointment-status-public-commentary"
            :label="$t('homelab.labels.public_commentary')"
            type="textarea"
            :min-rows="3"
            :placeholder="$t('homelab.messages.public_commentary_hint')"
            v-model="form.data.public_commentary"
            @input="form.errors.clear('public_commentary')"
            :error="form.errors.has('public_commentary')"
            :error-messages="[$t(form.errors.get('public_commentary'))]"
          />
        </div>
      </div>
    </form>

  </va-modal>
</template>

<script>
import Multiselect from 'vue-multiselect'
import Errors from '@/utils/CustomHomelabErrors'
import DatePicker from '@/components/common/DatePicker'
import { AppointmentStatusService } from '@/api/HomelabService'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import AppointmentShortInfo from '@/components/homelab/appointments/details/AppointmentShortInfo'

export default {
  props: {
    appointment: {
      type: Object,
      required: true
    }
  },
  components: {
    DatePicker,
    Multiselect,
    FadeTransition,
    AppointmentShortInfo
  },
  data: function () {
    return {
      isOpen: false,
      isSubmitingForm: false,
      isShowingAppointmentInfo: false,
      durations: [
        { 'id': 1, 'value': 15 },
        { 'id': 2, 'value': 30 },
        { 'id': 3, 'value': 45 },
        { 'id': 4, 'value': 60 },
        { 'id': 6, 'value': 90 },
        { 'id': 5, 'value': 120 },
        { 'id': 7, 'value': 150 },
        { 'id': 8, 'value': 180 },
        { 'id': 9, 'value': 240 },
      ],
      statuses: [
        { 'id': 1, 'name': 'homelab.statuses.1' },
        { 'id': 2, 'name': 'homelab.statuses.2' },
        { 'id': 3, 'name': 'homelab.statuses.3' },
        { 'id': 4, 'name': 'homelab.statuses.4' },
        { 'id': 5, 'name': 'homelab.statuses.5' },
        { 'id': 6, 'name': 'homelab.statuses.6' },
        { 'id': 20, 'name': 'homelab.statuses.20' }
      ],
      selectedStatus: null,
      scheduledTime: null,
      selectedDuration: { 'id': 1, 'value': 15 },
      form: {
        data: {
          status_id: null,
          private_commentary: null,
          public_commentary: null,
          date: this.moment().format('YYYY-MM-DD'),
          scheduled_time_start: null,
          scheduled_time_end: null
        },
        errors: new Errors()
      }
    }
  },
  computed: {
    availableStatuses: function () {
      if (this.appointment.status.id === 1) {
        return [
          this.statuses[1],
          this.statuses[2],
          this.statuses[6]
        ]
      } else {
        return [...this.statuses.slice(1)]
      }
    },
    appointmentIsAlreadyScheduled: function () {
      return (this.appointment.status.id === 2 || this.appointment.status.id === 3) && this.appointment.scheduled_time_start !== null
    },
    isRescheduling: function () {
      return this.selectedStatus && (this.selectedStatus.id === 2 || this.selectedStatus.id === 3)
    },
    formattedCurrentScheduleWarning: function () {
      if (this.appointmentIsAlreadyScheduled) {
        let schedule = this.getFormatedScheduledTime(this.appointment.scheduled_time_start, this.appointment.scheduled_time_end)
        return this.$t('homelab.messages.appointment_already_scheduled', { schedule })
      } else {
        return null
      }
    }
  },
  mounted: function () {
    this.isOpen = true
    if (this.appointment.status.id === 1) {
      console.log(this.moment(this.appointment.selected_time), this.moment().utc())
      let selectedTimeAsLocalTime = this.$options.filters.localTime(this.appointment.selected_time, 'YYYY-MM-DD HH:mm')
      if (this.moment(selectedTimeAsLocalTime).isAfter(this.moment())) {
        this.scheduledTime = selectedTimeAsLocalTime
      } else {
        this.scheduledTime = this.moment().format('YYYY-MM-DD HH:mm')
      }
    } else if (this.appointmentIsAlreadyScheduled) {
      let start = this.moment(this.$options.filters.localTime(this.appointment.scheduled_time_start))
      let end = this.moment(this.$options.filters.localTime(this.appointment.scheduled_time_end))
      this.scheduledTime = start.format('YYYY-MM-DD HH:mm')

      let minutesBetweenDates = end.diff(start, 'minutes')
      let duration = this.durations.find(d => d.value === minutesBetweenDates)
      if (duration === undefined) {
        duration = this.durations[0]
      }
      this.selectedDuration = duration
    }
  },
  methods: {
    onCancel: function () {
      this.isOpen = false
      setTimeout(() => {
        this.$emit('cancel')
      }, 500)
    },
    submit: function () {
      if (!this.isSubmitingForm) {
        this.isSubmitingForm = true
        AppointmentStatusService.store(this.appointment.id, this.form.data)
          .then(appointment => {
            this.$emit('appointment-status-updated', { status: appointment.status, history: appointment.status_history })
            this.$refs.updateAppointmentStatusModal.cancel()
            this.showToast(this.$t('homelab.messages.appointment_status_updated'), this.$store.state.successToastOptions)
          })
          .catch(errors => {
            let message = null
            if (errors.response.status === 422) {
              message = this.$t('homelab.errors.form_contains_errors')
              this.form.errors.record(errors.response.data.errors, 'homelab.errors.')
            } else if (errors.response.status === 403) {
              message = this.$t('homelab.errors.missing_permissions')
            } else {
              message = this.$t('homelab.something_went_wrong')
            }
            this.showToast(message, this.$store.state.errorToastOptions)
          })
          .finally(() => {
            this.isSubmitingForm = true
          })
      }
    },
    getFormatedSelectedTime: function (date) {
      date = this.moment(this.$options.filters.localTime(date))
      return `${date.format('YYYY-MM-DD')} ${this.$t('date_helpers.hour_to')} ${date.format('HH:mm')}`
    },
    getFormatedScheduledTime: function (start, end) {
      if (start && end) {
        start = this.moment(this.$options.filters.localTime(start))
        end = this.moment(this.$options.filters.localTime(end))
        return `${start.format('YYYY-MM-DD')} ${this.$t('date_helpers.hour_from')} ${start.format('HH:mm')} ${this.$t('date_helpers.hour_to')} ${end.format('HH:mm')}`
      } else {
        return this.$t('homelab.unassigned')
      }
    },
    getFormatedAppointmentType: function (appointment) {
      if (appointment.packages.length > 0) {
        let packages = []
        let charCounter = 0
        let hasMorePackages = false

        for (let i = 0; i < appointment.packages.length; i++) {
          if (charCounter + appointment.packages[i].name.length <= 50) {
            packages.push(appointment.packages[i].name)
          } else {
            hasMorePackages = i < appointment.packages.length
            i = appointment.packages.length
          }
        }

        let formatedAppointmentType = packages.join('; ')

        if (hasMorePackages) {
          formatedAppointmentType = `${formatedAppointmentType} (...)`
        }

        return formatedAppointmentType
      } else if (appointment.insurer) {
        if (appointment.insurer.id === 1) {
          return appointment.insurer.short_name
        } else {
          return `${this.$t('homelab.insurer')} - ${appointment.insurer.short_name}`
        }
      }
    },
    getAge: function (birthdate) {
      return this.moment().diff(this.moment(birthdate), 'year')
    },
    setScheduledPeriod: function () {
      if (this.scheduledTime) {
        let scheduledTimeStart = this.moment(this.$options.filters.utcTime(this.scheduledTime, 'YYYY-MM-DD HH:mm'))
        let scheduledTimeEnd = this.moment(this.$options.filters.utcTime(this.scheduledTime, 'YYYY-MM-DD HH:mm'))

        scheduledTimeEnd.add(this.selectedDuration.value, 'minutes')
        this.form.data.scheduled_time_start = scheduledTimeStart.format('YYYY-MM-DD HH:mm:ss')
        this.form.data.scheduled_time_end = scheduledTimeEnd.format('YYYY-MM-DD HH:mm:ss')
      }
    }
  },
  watch: {
    selectedStatus: function (value) {
      this.form.data.status_id = value ? value.id : null
    },
    scheduledTime: {
      immediate: true,
      handler: function () { this.setScheduledPeriod() }
    },
    selectedDuration: {
      immediate: true,
      handler: function () { this.setScheduledPeriod() }
    }
  }
}
</script>
